import {
  doc,
  updateDoc,
  arrayUnion,
  Timestamp,
  getDoc,
  collection,
  query,
  where,
  getDocs,
  writeBatch
} from 'firebase/firestore';
import {
  BENEFICIARY_STATUS,
  isTransitionAllowed,
  getStatusRules,
  SUSPENSION_REASONS,
  ARCHIVE_REASONS
} from '../constants/beneficiaryStatuses';

/**
 * Service de gestion des statuts des bénéficiaires
 * Gère tous les changements de statut avec validation et historique
 */
class BeneficiaryStatusService {
  constructor(db) {
    this.db = db;
  }

  /**
   * Change le statut d'un bénéficiaire avec validation complète
   * @param {string} beneficiaryId - ID du bénéficiaire
   * @param {string} newStatus - Nouveau statut
   * @param {object} options - Options du changement
   * @param {string} options.reason - Raison du changement
   * @param {string} options.userId - ID de l'utilisateur qui fait le changement
   * @param {Function} options.hasPermission - Fonction de vérification des permissions
   * @param {Date} options.endDate - Date de fin (pour suspension)
   * @param {Date} options.archiveDate - Date d'archivage (pour archivage)
   * @param {string} options.notes - Notes additionnelles
   * @returns {Promise<void>}
   */
  async changeStatus(beneficiaryId, newStatus, options = {}) {
    const { reason, userId, hasPermission, endDate, archiveDate, notes = '' } = options;

    console.log('🔄 Changement de statut bénéficiaire:', {
      beneficiaryId,
      newStatus,
      reason
    });

    // ===== VALIDATION DES PERMISSIONS =====
    if (!hasPermission || !hasPermission({ resource: 'beneficiaries', action: 'manage' })) {
      throw new Error('❌ Vous n\'avez pas les droits pour modifier le statut des bénéficiaires');
    }

    // ===== VALIDATION DE BASE =====
    if (!Object.values(BENEFICIARY_STATUS).includes(newStatus)) {
      throw new Error(`❌ Statut invalide: ${newStatus}`);
    }

    if (!beneficiaryId || !beneficiaryId.trim()) {
      throw new Error('❌ ID bénéficiaire manquant');
    }

    // ===== RÉCUPÉRATION DU BÉNÉFICIAIRE =====
    const beneficiaryRef = doc(this.db, 'clients', beneficiaryId);
    const beneficiarySnap = await getDoc(beneficiaryRef);

    if (!beneficiarySnap.exists()) {
      throw new Error(`❌ Bénéficiaire non trouvé: ${beneficiaryId}`);
    }

    const beneficiary = beneficiarySnap.data();
    const currentStatus = beneficiary.status || BENEFICIARY_STATUS.ACTIVE;

    // ===== VALIDATION DE LA TRANSITION =====
    if (!isTransitionAllowed(currentStatus, newStatus)) {
      throw new Error(`❌ Transition non autorisée de "${currentStatus}" vers "${newStatus}"`);
    }

    // ===== VALIDATIONS SPÉCIFIQUES PAR STATUT =====
    if (newStatus === BENEFICIARY_STATUS.SUSPENDED) {
      if (!reason || !reason.trim()) {
        throw new Error('❌ La raison de suspension est obligatoire');
      }
      if (!endDate) {
        throw new Error('❌ La date de fin de suspension est obligatoire');
      }
      if (new Date(endDate) <= new Date()) {
        throw new Error('❌ La date de fin de suspension doit être dans le futur');
      }
    }

    if (newStatus === BENEFICIARY_STATUS.ARCHIVED) {
      if (!reason || !reason.trim()) {
        throw new Error('❌ La raison d\'archivage est obligatoire');
      }
      if (!archiveDate) {
        throw new Error('❌ La date d\'archivage est obligatoire');
      }
    }

    // ===== PRÉPARATION DES DONNÉES DE MISE À JOUR =====
    const now = Timestamp.now();
    const statusChange = {
      oldStatus: currentStatus,
      newStatus: newStatus,
      reason: reason || '',
      notes: notes,
      changedBy: userId || 'system',
      changedAt: now
    };

    const updates = {
      status: newStatus,
      statusHistory: arrayUnion(statusChange),
      lastStatusChange: now,
      updatedAt: now
    };

    // ===== DONNÉES SPÉCIFIQUES PAR STATUT =====
    switch (newStatus) {
      case BENEFICIARY_STATUS.SUSPENDED:
        updates.suspensionDetails = {
          reason: reason,
          startDate: now,
          endDate: Timestamp.fromDate(new Date(endDate)),
          notes: notes,
          suspendedBy: userId || 'system'
        };
        break;

      case BENEFICIARY_STATUS.ARCHIVED:
        updates.isArchived = true;
        updates.archiveDate = Timestamp.fromDate(new Date(archiveDate));
        updates.archiveReason = reason;
        updates.archivedAt = now;
        updates.archivedBy = userId || 'system';
        break;

      case BENEFICIARY_STATUS.ACTIVE:
        // Mettre à jour l'ancien système pour compatibilité
        updates.isActive = true;

        // Nettoyage des données de suspension si réactivation
        if (currentStatus === BENEFICIARY_STATUS.SUSPENDED) {
          updates.suspensionDetails = null;
        }
        // Nettoyage des données d'archivage si réactivation (ne devrait pas arriver)
        if (currentStatus === BENEFICIARY_STATUS.ARCHIVED) {
          updates.isArchived = false;
          updates.archiveDate = null;
          updates.archiveReason = null;
        }
        // Nettoyage des données d'inactivité si réactivation
        if (currentStatus === BENEFICIARY_STATUS.INACTIVE) {
          updates.inactiveDetails = null;
        }
        break;

      case BENEFICIARY_STATUS.INACTIVE:
        // Mettre à jour l'ancien système pour compatibilité
        updates.isActive = false;

        updates.inactiveDetails = {
          reason: reason,
          startDate: now,
          notes: notes,
          markedBy: userId || 'system'
        };
        break;

      case BENEFICIARY_STATUS.PENDING:
        updates.pendingDetails = {
          reason: reason,
          startDate: now,
          notes: notes,
          markedBy: userId || 'system'
        };
        break;
    }

    // ===== GESTION DES RENDEZ-VOUS =====
    if (newStatus === BENEFICIARY_STATUS.SUSPENDED) {
      // Suspension: toujours annuler les rendez-vous
      await this._handleAppointmentsCancellation(beneficiaryId, newStatus, new Date());
    } else if (newStatus === BENEFICIARY_STATUS.ARCHIVED && data.deleteAppointments !== false) {
      // Archivage: supprimer si demandé (par défaut: true)
      await this._handleAppointmentsCancellation(beneficiaryId, newStatus, archiveDate || new Date());
    }

    // ===== MISE À JOUR DU BÉNÉFICIAIRE =====
    await updateDoc(beneficiaryRef, updates);

    console.log('✅ Statut bénéficiaire modifié avec succès:', {
      beneficiaryId,
      oldStatus: currentStatus,
      newStatus,
      reason
    });

    // ===== NOTIFICATIONS =====
    // Les notifications seront gérées par le composant appelant
    // car elles dépendent du contexte utilisateur (ToastContext, etc.)

    return {
      success: true,
      oldStatus: currentStatus,
      newStatus: newStatus,
      beneficiary: beneficiary
    };
  }

  /**
   * Suspend un bénéficiaire
   * @param {string} beneficiaryId - ID du bénéficiaire
   * @param {string} reason - Raison de la suspension
   * @param {Date} endDate - Date de fin de suspension
   * @param {object} options - Options additionnelles
   * @returns {Promise<void>}
   */
  async suspendBeneficiary(beneficiaryId, reason, endDate, options = {}) {
    console.log('⏸ Suspension du bénéficiaire:', beneficiaryId);

    // Validation de la raison
    const validReasons = SUSPENSION_REASONS.map(r => r.value);
    const isValidReason = validReasons.includes(reason) || reason === 'autre';

    if (!isValidReason && options.customReason) {
      // Si raison personnalisée fournie
      reason = options.customReason;
    }

    return this.changeStatus(beneficiaryId, BENEFICIARY_STATUS.SUSPENDED, {
      ...options,
      reason,
      endDate
    });
  }

  /**
   * Active ou réactive un bénéficiaire
   * @param {string} beneficiaryId - ID du bénéficiaire
   * @param {object} options - Options additionnelles
   * @returns {Promise<void>}
   */
  async activateBeneficiary(beneficiaryId, options = {}) {
    console.log('✅ Activation du bénéficiaire:', beneficiaryId);

    return this.changeStatus(beneficiaryId, BENEFICIARY_STATUS.ACTIVE, {
      ...options,
      reason: options.reason || 'Activation du bénéficiaire'
    });
  }

  /**
   * Archive un bénéficiaire
   * @param {string} beneficiaryId - ID du bénéficiaire
   * @param {string} reason - Raison de l'archivage
   * @param {Date} archiveDate - Date d'archivage
   * @param {object} options - Options additionnelles
   * @returns {Promise<void>}
   */
  async archiveBeneficiary(beneficiaryId, reason, archiveDate, options = {}) {
    console.log('📦 Archivage du bénéficiaire:', beneficiaryId);

    // Validation de la raison
    const validReasons = ARCHIVE_REASONS.map(r => r.value);
    const isValidReason = validReasons.includes(reason) || reason === 'autre';

    if (!isValidReason && options.customReason) {
      // Si raison personnalisée fournie
      reason = options.customReason;
    }

    return this.changeStatus(beneficiaryId, BENEFICIARY_STATUS.ARCHIVED, {
      ...options,
      reason,
      archiveDate
    });
  }

  /**
   * Marque un bénéficiaire comme inactif
   * @param {string} beneficiaryId - ID du bénéficiaire
   * @param {object} options - Options additionnelles
   * @returns {Promise<void>}
   */
  async markAsInactive(beneficiaryId, options = {}) {
    console.log('● Marquage bénéficiaire comme inactif:', beneficiaryId);

    return this.changeStatus(beneficiaryId, BENEFICIARY_STATUS.INACTIVE, {
      ...options,
      reason: options.reason || 'Service en pause - à relancer'
    });
  }

  /**
   * Marque un bénéficiaire comme en attente
   * @param {string} beneficiaryId - ID du bénéficiaire
   * @param {object} options - Options additionnelles
   * @returns {Promise<void>}
   */
  async markAsPending(beneficiaryId, options = {}) {
    console.log('⏳ Marquage bénéficiaire en attente:', beneficiaryId);

    return this.changeStatus(beneficiaryId, BENEFICIARY_STATUS.PENDING, {
      ...options,
      reason: options.reason || 'Dossier incomplet - en cours d\'intégration'
    });
  }

  /**
   * Récupère l'historique des changements de statut
   * @param {string} beneficiaryId - ID du bénéficiaire
   * @returns {Promise<Array>} Historique des changements
   */
  async getStatusHistory(beneficiaryId) {
    const beneficiaryRef = doc(this.db, 'clients', beneficiaryId);
    const beneficiarySnap = await getDoc(beneficiaryRef);

    if (!beneficiarySnap.exists()) {
      throw new Error(`❌ Bénéficiaire non trouvé: ${beneficiaryId}`);
    }

    const beneficiary = beneficiarySnap.data();
    return beneficiary.statusHistory || [];
  }

  /**
   * Vérifie si un bénéficiaire peut être facturé
   * @param {string} beneficiaryId - ID du bénéficiaire
   * @returns {Promise<boolean>}
   */
  async canBeBilled(beneficiaryId) {
    const beneficiaryRef = doc(this.db, 'clients', beneficiaryId);
    const beneficiarySnap = await getDoc(beneficiaryRef);

    if (!beneficiarySnap.exists()) {
      return false;
    }

    const beneficiary = beneficiarySnap.data();
    const status = beneficiary.status || BENEFICIARY_STATUS.ACTIVE;
    const rules = getStatusRules(status);

    return rules.allowBilling;
  }

  /**
   * Récupère les bénéficiaires suspendus dont la fin de suspension approche
   * @param {number} daysThreshold - Nombre de jours avant la fin
   * @returns {Promise<Array>} Liste des bénéficiaires
   */
  async getSuspendedEndingSoon(daysThreshold = 7) {
    const clientsRef = collection(this.db, 'clients');
    const q = query(
      clientsRef,
      where('status', '==', BENEFICIARY_STATUS.SUSPENDED)
    );

    const snapshot = await getDocs(q);
    const now = new Date();
    const thresholdDate = new Date(now.getTime() + daysThreshold * 24 * 60 * 60 * 1000);

    const beneficiaries = [];
    snapshot.forEach(doc => {
      const data = doc.data();
      if (data.suspensionDetails?.endDate) {
        const endDate = data.suspensionDetails.endDate.toDate();
        if (endDate >= now && endDate <= thresholdDate) {
          beneficiaries.push({ id: doc.id, ...data });
        }
      }
    });

    return beneficiaries;
  }

  /**
   * Récupère les bénéficiaires inactifs depuis plus de X jours
   * @param {number} daysThreshold - Nombre de jours d'inactivité
   * @returns {Promise<Array>} Liste des bénéficiaires
   */
  async getInactiveLongTerm(daysThreshold = 30) {
    const clientsRef = collection(this.db, 'clients');
    const q = query(
      clientsRef,
      where('status', '==', BENEFICIARY_STATUS.INACTIVE)
    );

    const snapshot = await getDocs(q);
    const now = new Date();
    const thresholdDate = new Date(now.getTime() - daysThreshold * 24 * 60 * 60 * 1000);

    const beneficiaries = [];
    snapshot.forEach(doc => {
      const data = doc.data();
      if (data.inactiveDetails?.startDate) {
        const startDate = data.inactiveDetails.startDate.toDate();
        if (startDate <= thresholdDate) {
          beneficiaries.push({ id: doc.id, ...data });
        }
      }
    });

    return beneficiaries;
  }

  /**
   * Réactive automatiquement les bénéficiaires suspendus dont la date de fin est atteinte
   * Cette fonction est appelée par une Cloud Function planifiée
   * @returns {Promise<Array>} Liste des bénéficiaires réactivés
   */
  async autoReactivateSuspended() {
    console.log('🔄 Vérification des réactivations automatiques...');

    const clientsRef = collection(this.db, 'clients');
    const q = query(
      clientsRef,
      where('status', '==', BENEFICIARY_STATUS.SUSPENDED)
    );

    const snapshot = await getDocs(q);
    const now = new Date();
    const reactivated = [];

    for (const docSnap of snapshot.docs) {
      const data = docSnap.data();
      if (data.suspensionDetails?.endDate) {
        const endDate = data.suspensionDetails.endDate.toDate();

        // Si la date de fin est passée
        if (endDate <= now) {
          try {
            await this.changeStatus(docSnap.id, BENEFICIARY_STATUS.ACTIVE, {
              reason: 'Réactivation automatique après fin de suspension',
              userId: 'system',
              hasPermission: () => true // System bypass
            });

            reactivated.push({
              id: docSnap.id,
              nom: data.nom,
              endDate: endDate
            });

            console.log(`✅ Bénéficiaire ${docSnap.id} réactivé automatiquement`);
          } catch (error) {
            console.error(`❌ Erreur réactivation ${docSnap.id}:`, error);
          }
        }
      }
    }

    console.log(`✅ ${reactivated.length} bénéficiaires réactivés automatiquement`);
    return reactivated;
  }

  /**
   * Gère l'annulation des rendez-vous pour suspension ou archivage
   * @private
   * @param {string} beneficiaryId - ID du bénéficiaire
   * @param {string} newStatus - Nouveau statut
   * @param {Date} effectiveDate - Date effective (archive ou suspension)
   * @returns {Promise<void>}
   */
  async _handleAppointmentsCancellation(beneficiaryId, newStatus, effectiveDate) {
    console.log('📅 Gestion des rendez-vous pour changement de statut:', {
      beneficiaryId,
      newStatus,
      effectiveDate
    });

    const appointmentsRef = collection(this.db, 'appointments');

    // Récupérer tous les rendez-vous futurs du bénéficiaire
    const q = query(
      appointmentsRef,
      where('clientId', '==', beneficiaryId)
    );

    const snapshot = await getDocs(q);
    const batch = writeBatch(this.db);
    let cancelledCount = 0;
    let deletedCount = 0;

    const effectiveTimestamp = Timestamp.fromDate(new Date(effectiveDate));

    snapshot.forEach(docSnap => {
      const appointment = docSnap.data();
      const appointmentDate = appointment.date;

      // Si rendez-vous après la date effective
      if (appointmentDate >= effectiveTimestamp) {
        if (newStatus === BENEFICIARY_STATUS.ARCHIVED) {
          // Pour archivage: supprimer les rendez-vous
          batch.delete(docSnap.ref);
          deletedCount++;
        } else if (newStatus === BENEFICIARY_STATUS.SUSPENDED) {
          // Pour suspension: marquer comme annulés
          batch.update(docSnap.ref, {
            status: 'cancelled',
            cancelReason: 'Bénéficiaire suspendu',
            cancelledAt: Timestamp.now()
          });
          cancelledCount++;
        }
      }
    });

    if (cancelledCount > 0 || deletedCount > 0) {
      await batch.commit();
      console.log(`✅ Rendez-vous traités: ${cancelledCount} annulés, ${deletedCount} supprimés`);
    } else {
      console.log('ℹ️ Aucun rendez-vous futur à traiter');
    }
  }

  /**
   * Récupère les statistiques des statuts des bénéficiaires
   * @returns {Promise<object>} Statistiques par statut
   */
  async getStatusStatistics() {
    const clientsRef = collection(this.db, 'clients');
    const snapshot = await getDocs(clientsRef);

    const stats = {
      [BENEFICIARY_STATUS.ACTIVE]: 0,
      [BENEFICIARY_STATUS.PENDING]: 0,
      [BENEFICIARY_STATUS.SUSPENDED]: 0,
      [BENEFICIARY_STATUS.INACTIVE]: 0,
      [BENEFICIARY_STATUS.ARCHIVED]: 0,
      total: 0
    };

    snapshot.forEach(doc => {
      const data = doc.data();
      const status = data.status || BENEFICIARY_STATUS.ACTIVE;

      if (stats.hasOwnProperty(status)) {
        stats[status]++;
      }
      stats.total++;
    });

    return stats;
  }
}

// Export singleton
let serviceInstance = null;

export const getBeneficiaryStatusService = (db) => {
  if (!serviceInstance || serviceInstance.db !== db) {
    serviceInstance = new BeneficiaryStatusService(db);
  }
  return serviceInstance;
};

export default BeneficiaryStatusService;
